export function formatMoney(amount, currency, { compact = false } = {}) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return `${currency || ""} -`;
  // compact drops decimals (tight spaces like the suggestions cards); default
  // keeps cents for invoices/refunds where they matter.
  return `${currency || ""} ${num.toFixed(compact ? 0 : 2)}`.trim();
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function minutesLabel(minutes) {
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return `${h} ${h === 1 ? "hour" : "hours"}`;
  }
  if (minutes < 60) return `${minutes} minutes`;
  const h = Math.floor(minutes / 60);
  return `${h} ${h === 1 ? "hour" : "hours"} ${minutes % 60} minutes`;
}

/**
 * The club's check-in rule in a sentence, or null when check-in is off — the
 * server sends no policy at all then, so nothing about check-in is shown.
 *
 * END_OF_DAY keeps the window open until midnight club time so a client who
 * played and forgot can still check in on the way out; END_OF_SESSION closes it
 * `closeMinutesAfter` past the end.
 */
export function checkInPolicyText(policy) {
  if (!policy) return null;

  const opens =
    policy.openMinutesBefore > 0 ? `from ${minutesLabel(policy.openMinutesBefore)} before your session starts` : "as soon as your session starts";

  const closes =
    policy.closeMode === "END_OF_SESSION"
      ? policy.closeMinutesAfter > 0
        ? `up to ${minutesLabel(policy.closeMinutesAfter)} after it ends`
        : "up until it ends"
      : "any time until the end of that day";

  return `Check in ${opens}, and ${closes}.`;
}

/**
 * What a refund gave back, in a sentence. A wallet or package refund stores 0 as its
 * invoice total, so the server sends `refund.amount` and where it went instead.
 * A package refund returns a session, not money, so it never quotes an amount.
 */
export function refundSummary(refund, currency) {
  if (!refund) return "";
  const amount = formatMoney(refund.amount, currency);
  // Raised but the money has not moved yet — the club finishes it from the desk.
  if (refund.pending) return refund.destination === "CARD" ? `${amount} refund to your card in progress` : `${amount} refund in progress`;
  switch (refund.destination) {
    case "PACKAGE":
      return "Session returned to your package";
    case "WRITEOFF":
      return "Cancelled — nothing was owed";
    case "CARD":
      return `${amount} refunded to your card`;
    case "CASH":
      return `${amount} refunded in cash`;
    default:
      return `${amount} added to your wallet`;
  }
}

/**
 * The pill an invoice wears. A refund invoice is stored as type REFUND whatever it did,
 * so a write-off (nothing was ever paid) and a refund still waiting on the gateway each
 * get their own pill rather than "REFUND".
 */
export function invoicePill(invoice) {
  const refund = invoice?.refund;
  if (!refund) return invoice?.status;
  if (refund.destination === "WRITEOFF") return "WRITEOFF";
  return refund.pending ? "REFUND_PENDING" : "REFUND";
}

// A class the client cancelled and was re-added to lists two rows under one booking _id
// (the cancelled seat and the new one), so rows are keyed by seat.
export const bookingRowKey = (booking) => booking.bookingClientId || booking._id;

export { formatDuration } from "/imports/libs/format";
